import { useLayoutEffect, useRef } from 'react'
import { gsap, registerGsap } from '../anim/gsapSetup'
import { useReducedMotion } from '../anim/useReducedMotion'
import { Seo } from '../components/Seo'
import { PageHeader } from '../components/common/PageHeader'
import { Section } from '../components/common/Section'
import { Eyebrow } from '../components/common/Eyebrow'
import { SplitHeading } from '../components/animation/SplitHeading'
import { Reveal } from '../components/animation/Reveal'
import { Icon } from '../components/common/Icon'
import { Button } from '../components/common/Button'
import { LeafMark } from '../components/common/Logo'
import { company } from '../data/company'
import { organizationSchema, breadcrumbSchema } from '../lib/seo'

function Timeline() {
  const scopeRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()
  useLayoutEffect(() => {
    const scope = scopeRef.current
    if (!scope || reduced) return
    registerGsap()
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '[data-tl="line"]',
        { scaleY: 0 },
        { scaleY: 1, ease: 'none', scrollTrigger: { trigger: scope, start: 'top 70%', end: 'bottom 80%', scrub: true } },
      )
      gsap.from('[data-tl="item"]', {
        opacity: 0,
        y: 30,
        stagger: 0.2,
        duration: 0.7,
        ease: 'power3.out',
        scrollTrigger: { trigger: scope, start: 'top 70%' },
      })
    }, scope)
    return () => ctx.revert()
  }, [reduced])

  return (
    <div ref={scopeRef} className="relative mt-10 pl-10">
      <div className="absolute left-[14px] top-2 h-full w-px bg-sand-200">
        <div data-tl="line" className="h-full w-full origin-top bg-brand-500" />
      </div>
      <ol className="space-y-10">
        {company.milestones.map((m) => (
          <li key={m.title} data-tl="item" className="relative">
            <span className="absolute left-[-2.5rem] top-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-brand-500 bg-canvas">
              <span className="h-2.5 w-2.5 rounded-full bg-brand-500" />
            </span>
            <span className="text-eyebrow font-semibold uppercase tracking-[0.18em] text-brand-600">{m.label}</span>
            <h3 className="mt-1.5 font-display text-xl font-semibold text-ink">{m.title}</h3>
            <p className="mt-1.5 max-w-xl text-ink-500">{m.body}</p>
          </li>
        ))}
      </ol>
    </div>
  )
}

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About GreenbayBio — Sustainable Bio-Innovation for Indian Farmers"
        description={`${company.legalName} develops bio-fertilizers, biostimulants and plant-strength solutions rooted in soil biology. Learn our mission, vision and approach.`}
        path="/about"
        jsonLd={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }])]}
      />
      <PageHeader
        eyebrow="About us"
        title="Rooted in soil biology, built for Indian farmers."
        subtitle={company.tagline}
      />

      {/* intro */}
      <Section className="section bg-canvas" containerClassName="grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
        <div>
          <Eyebrow>Who we are</Eyebrow>
          <SplitHeading
            as="h2"
            text="We believe healthy crops begin with healthy soil."
            className="mt-4 font-display text-h1 font-semibold text-ink"
          />
        </div>
        <Reveal className="space-y-4 text-lg leading-relaxed text-ink-700" stagger={0.1} y={20}>
          <p className="text-xl text-ink">{company.description}</p>
          <p>{company.mission}</p>
        </Reveal>
      </Section>

      {/* mission / vision */}
      <Section className="bg-sand-100/50 py-section" containerClassName="grid gap-6 md:grid-cols-2">
        {[
          { icon: 'Sprout', label: 'Mission', body: company.mission },
          { icon: 'Sun', label: 'Vision', body: company.vision },
        ].map((b) => (
          <Reveal key={b.label} className="rounded-2xl border border-sand-200 bg-white p-8 shadow-soft" y={24}>
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
              <Icon name={b.icon} className="h-6 w-6" />
            </span>
            <h2 className="mt-5 font-display text-h3 font-semibold text-ink">{b.label}</h2>
            <p className="mt-3 leading-relaxed text-ink-600">{b.body}</p>
          </Reveal>
        ))}
      </Section>

      {/* philosophy */}
      <Section className="grain relative overflow-hidden bg-brand-900 py-section text-white">
        <LeafMark className="pointer-events-none absolute -right-10 -top-10 h-80 w-80 rotate-12 text-white/[0.05]" />
        <div className="container-x relative max-w-4xl">
          <Icon name="Quote" className="h-10 w-10 text-brand-300" />
          <blockquote className="mt-6 font-display text-3xl font-medium leading-snug text-white md:text-4xl">
            “{company.philosophy.quote}”
          </blockquote>
          <cite className="mt-6 block not-italic text-white/60">— {company.philosophy.author}</cite>
        </div>
      </Section>

      {/* approach timeline */}
      <Section className="section bg-canvas" containerClassName="">
        <div className="max-w-2xl">
          <Eyebrow>Our approach</Eyebrow>
          <SplitHeading as="h2" text="How GreenbayBio works." className="mt-4 font-display text-h1 font-semibold text-ink" />
        </div>
        <Timeline />
      </Section>

      {/* values */}
      <Section className="bg-sand-100/50 py-section" containerClassName="">
        <div className="max-w-2xl">
          <Eyebrow>What we stand for</Eyebrow>
          <h2 className="mt-4 font-display text-h1 font-semibold text-ink">Our values</h2>
        </div>
        <Reveal className="mt-10 grid gap-4 md:grid-cols-2" stagger={0.1} y={24}>
          {company.values.map((v) => (
            <div key={v.title} className="flex gap-5 rounded-xl border border-sand-200 bg-white p-7 shadow-soft">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                <Icon name={v.icon} className="h-6 w-6" />
              </span>
              <div>
                <h3 className="font-display text-lg font-semibold text-ink">{v.title}</h3>
                <p className="mt-1.5 leading-relaxed text-ink-500">{v.body}</p>
              </div>
            </div>
          ))}
        </Reveal>
      </Section>

      {/* partners + certifications */}
      <Section className="section bg-canvas" containerClassName="grid gap-12 lg:grid-cols-2">
        <div>
          <Eyebrow>Science partners</Eyebrow>
          <h2 className="mt-4 font-display text-h2 font-semibold text-ink">Global science, adapted for India</h2>
          <p className="mt-3 max-w-lg text-ink-500">
            We work with pioneers in soil biology, humic science, botanical extracts and moisture management.
          </p>
          <ul className="mt-6 space-y-3">
            {company.partners.map((p) => (
              <li key={p.name} className="flex items-center gap-3 rounded-lg border border-sand-200 bg-white p-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                  <Icon name="Handshake" className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-semibold text-ink">{p.name}</p>
                  <p className="text-sm text-ink-500">{p.note}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <Eyebrow>Approvals</Eyebrow>
          <h2 className="mt-4 font-display text-h2 font-semibold text-ink">Certified where it counts</h2>
          <p className="mt-3 max-w-lg text-ink-500">Approvals vary by product — each product page lists its specific licence.</p>
          <ul className="mt-6 space-y-3">
            {company.certifications.map((c) => (
              <li key={c} className="flex items-start gap-3 rounded-lg border border-brand-200 bg-brand-50/50 p-4 text-brand-900">
                <Icon name="ShieldCheck" className="mt-0.5 h-5 w-5 shrink-0 text-brand-500" />
                <span className="text-sm">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* CTA */}
      <Section className="bg-sand-100/50 py-section" containerClassName="rounded-none text-center">
        <h2 className="mx-auto max-w-2xl font-display text-h1 font-semibold text-ink">
          Want to grow with GreenbayBio?
        </h2>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Button to="/catalog" variant="primary" size="lg">
            Explore products <Icon name="ArrowRight" className="h-5 w-5" />
          </Button>
          <Button to="/contact" variant="outline" size="lg">
            Contact us
          </Button>
        </div>
      </Section>
    </>
  )
}
